import type { CSSProperties } from "react"
import { Link, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta, homeJsonLd } from "@/components/public/PublicMeta"
import { HOW_IT_WORKS_STEPS } from "@/components/public/how-it-works-steps"
import { WineCard, type WineCardData } from "@/components/public/WineCard"
import type { PageProps } from "@/types/inertia"
import heroWedding from "@/images/hero-wedding.webp"
import labelClub from "@/images/label-club.webp"
import labelGolf from "@/images/label-golf.webp"
import labelHospitality from "@/images/label-hospitality.webp"
import labelSchool from "@/images/label-school.webp"

// Keyword-led (M7 SEO pass); already names the offer, so no site-name suffix.
const TITLE = "Bespoke Wine Labels UK | Premium South African Wine"
const DESCRIPTION =
  "Luxury South African wine with your own bespoke label, for weddings, corporate gifts, hospitality and private events, delivered throughout the UK."

const MARQUE = ["Wines of the Cape", "Bespoke Labels", "Low Minimum Orders", "Delivered UK-wide"]

// Occasion tile imagery is the mockup's placeholder renders — replace after
// Duncan's photo shoot. --s / --ox / --oy crop each render to its bottle.
type Occasion = {
  href: string
  title: string
  summary: string
  image: string
  alt: string
  crop: { s: number; ox: string; oy: string }
}

const OCCASIONS: Occasion[] = [
  {
    href: "/occasions/corporate",
    title: "Corporate",
    summary: "Gifts, launches and recognition.",
    image: labelHospitality,
    alt: "Bespoke corporate gift wine label",
    crop: { s: 2.25, ox: "28%", oy: "52%" },
  },
  {
    href: "/occasions/weddings",
    title: "Weddings",
    summary: "Table wines, favours, keepsakes.",
    image: heroWedding,
    alt: "Bespoke wedding wine label",
    crop: { s: 1.85, ox: "51%", oy: "56%" },
  },
  {
    href: "/occasions/hospitality",
    title: "Hospitality",
    summary: "House wines and private labels.",
    image: labelClub,
    alt: "Bespoke members' club house wine label",
    crop: { s: 2.25, ox: "28%", oy: "52%" },
  },
  {
    href: "/occasions/private-events",
    title: "Private Events",
    summary: "Milestones worth keeping.",
    image: labelSchool,
    alt: "Bespoke private celebration wine label",
    crop: { s: 2.25, ox: "27%", oy: "52%" },
  },
  {
    href: "/occasions/sporting-events",
    title: "Sporting Events",
    summary: "Polo, racing, regattas, golf days.",
    image: labelGolf,
    alt: "Bespoke sporting event wine label",
    crop: { s: 2.25, ox: "28%", oy: "52%" },
  },
]

// Duncan's real bottles (RAC, West Midlands Golf Club, Reed's School),
// captioned discreetly as in the mockup.
const LABELS = [
  { image: labelClub, alt: "Members' club commemorative bottle", title: "Members' Club", place: "London" },
  { image: labelGolf, alt: "Golf club captains' day commemorative bottle", title: "Captains' Day", place: "West Midlands" },
  { image: labelSchool, alt: "School foundation commemorative bottle", title: "Foundation Dinner", place: "Surrey" },
]

const ROMAN = ["I", "II", "III", "IV", "V"]

// The "From the cellar" band needs at least three featured wines; with fewer
// (production, until Duncan curates them) the whole band is left out.
const FEATURED_MINIMUM = 3
type HomeProps = { featured_wines?: WineCardData[]; mcc_explainer?: string }

function FeaturedWines({ wines, mccExplainer }: { wines: WineCardData[]; mccExplainer: string }) {
  if (wines.length < FEATURED_MINIMUM) return null

  // Charcoal, not black: on black it merged with the black "Labels we have
  // made" band below. The charcoal gradient restarts after Occasions, so the
  // two charcoal bands still read as separate, and the mockup's own
  // black/charcoal/black sequence from Labels onwards is untouched.
  return (
    <section className="band band-charcoal cellar-band" aria-labelledby="cellar-title">
      <div className="shell">
        <div className="band-head">
          <div className="eyebrow">Our wines</div>
          <h2 id="cellar-title">From the cellar</h2>
          <p className="lede">
            A few of the South African wines we carry, each ready to wear a label made for your
            occasion.
          </p>
        </div>
        <div className="cellar-grid">
          {wines.map((wine) => (
            <WineCard key={wine.id} wine={wine} mccExplainer={mccExplainer} showStepper={false} />
          ))}
        </div>
        <div className="cellar-more">
          <Button asChild variant="ghost-hairline" size="lg">
            <Link href="/wines">View Our Wines</Link>
          </Button>
        </div>
      </div>
    </section>
  )
}

export default function Home() {
  const { props } = usePage<PageProps<HomeProps>>()
  const featured = props.featured_wines ?? []

  return (
    <PublicShell>
      <PublicMeta
        title={TITLE}
        description={DESCRIPTION}
        brand={false}
        jsonLd={homeJsonLd(props.site_url, DESCRIPTION)}
      />

      <PublicHero
        eyebrow="Bespoke labels · Imported from South Africa"
        title="Premium South African wine. Your brand on the bottle."
        lede="Quietly exceptional wines from the Cape, dressed in a label made for your occasion and delivered anywhere in the United Kingdom."
        image={{
          src: heroWedding,
          alt: "A bespoke-labelled bottle of South African red wine on a candle-lit table setting",
          width: 1072,
          height: 1600,
        }}
        rule="Corporate · Weddings · Hospitality · Private & Sporting Events"
        actions={
          <>
            <Button asChild variant="gold" size="lg">
              <Link href="/studio">Design Your Label</Link>
            </Button>
            <Button asChild variant="ghost-hairline" size="lg">
              <Link href="/contact">Request a Consultation</Link>
            </Button>
          </>
        }
      />

      <div className="marque">
        <ul className="shell marque-grid" aria-label="Why SOH GD Wines">
          {MARQUE.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <section className="band band-charcoal" id="occasions" aria-labelledby="occasions-title">
        <div className="shell">
          <div className="band-head">
            <div className="eyebrow">Occasions</div>
            <h2 id="occasions-title">Every bottle belongs to a moment</h2>
            <p className="lede">
              Tell us the occasion and we will design a label that carries it — from a boardroom gift
              to a wedding table to the last day of a season.
            </p>
          </div>

          <div className="occ-grid">
            {OCCASIONS.map((occasion) => (
              <Link
                key={occasion.href}
                href={occasion.href}
                className="occ"
                style={
                  {
                    "--s": occasion.crop.s,
                    "--ox": occasion.crop.ox,
                    "--oy": occasion.crop.oy,
                  } as CSSProperties
                }
              >
                <img src={occasion.image} alt={occasion.alt} loading="lazy" width={941} height={1672} />
                <div className="occ-veil" />
                <div className="occ-body">
                  <h3>{occasion.title}</h3>
                  <span className="occ-summary">{occasion.summary}</span>
                  <span className="occ-arrow" aria-hidden="true">
                    Explore &rarr;
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FeaturedWines wines={featured} mccExplainer={props.mcc_explainer ?? ""} />

      <section className="band band-black" aria-labelledby="labels-title">
        <div className="shell">
          <div className="band-head band-head-center">
            <div className="eyebrow eyebrow-center">From the cellar</div>
            <h2 id="labels-title">Labels we have made</h2>
            <p className="lede">
              A small selection of bespoke bottles produced for clubs, schools and clubhouses across
              the United Kingdom.
            </p>
          </div>

          <div className="labels">
            {LABELS.map((label) => (
              <figure key={label.title} className="label-card">
                <div className="label-frame">
                  <img src={label.image} alt={label.alt} loading="lazy" width={941} height={1672} />
                </div>
                <figcaption className="label-meta">
                  <span className="label-title">{label.title}</span>
                  <span className="label-place">{label.place}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-14 flex justify-center">
            <Button asChild variant="ghost-hairline" size="sm">
              <Link href="/studio">Design your own</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="band band-charcoal" aria-labelledby="how-title">
        <div className="shell">
          <div className="band-head">
            <div className="eyebrow">How it works</div>
            <h2 id="how-title">Five unhurried steps</h2>
          </div>
          <ol className="steps">
            {HOW_IT_WORKS_STEPS.map((step, index) => (
              <li key={step.title} className="step">
                <span className="step-numeral" aria-hidden="true">
                  {ROMAN[index]}
                </span>
                <h3>{step.title}</h3>
                <p>{step.teaser}</p>
              </li>
            ))}
          </ol>
          <div className="steps-more">
            <Button asChild variant="ghost-hairline" size="sm">
              <Link href="/how-it-works">The process in full</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="band band-black" aria-labelledby="heritage-title">
        <div className="shell split">
          <div>
            <div className="eyebrow">South African heritage</div>
            <h2 id="heritage-title">Chosen quietly, from the Cape</h2>
            <p className="lede">
              Our wines come from 50+ South African winemakers through our partnership
              with Sideways Wine, selected for consistency and character rather than noise. Every
              bottle pairs that craftsmanship with a label made only for you.
            </p>
            <blockquote>Exceptional South African wines. Exclusively yours.</blockquote>
            <div className="split-actions">
              <Button asChild variant="ghost-hairline" size="lg">
                <Link href="/wines">Our Wines</Link>
              </Button>
            </div>
          </div>
          {/* Placeholder render — replace after Duncan's photo shoot. */}
          <div className="split-media">
            <img
              src={heroWedding}
              alt="Bespoke labelled bottle in a warm, low-lit setting"
              loading="lazy"
              width={1072}
              height={1600}
            />
          </div>
        </div>
      </section>

      <ConsultationBand brochure />
    </PublicShell>
  )
}
